import { IStorePackageVariation } from 'models/store/i-store-package-variation.interface';
import { IWorldNftTradeVariation } from 'models/store/i-world-nft-trade-response.interface';

// Generic type for any variation data
type VariationData = IStorePackageVariation | IWorldNftTradeVariation;
type VariationsRecord<T extends VariationData> = Record<string, T>;

/**
 * Get the first (base) variation from available_trade_offers_by_variation
 */
export const getBaseVariation = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null
): T | null => {
  if (!variations || typeof variations !== 'object') {
    return null;
  }

  const variationKeys = Object.keys(variations);
  if (variationKeys.length === 0) return null;

  const firstVariationKey = variationKeys[0];
  return variations[firstVariationKey];
};

/**
 * Get a specific variation by shares/multiplier
 */
export const getVariationByShares = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null,
  shares: number | string
): T | null => {
  if (!variations || typeof variations !== 'object') {
    return null;
  }

  const key = shares.toString();
  return variations[key] || null;
};

/**
 * Check if variations are sold out (no available quantity in base variation)
 */
export const areVariationsSoldOut = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null
): boolean => {
  const baseVariation = getBaseVariation(variations);
  if (!baseVariation) return true;

  return baseVariation.available_quantity === 0;
};

/**
 * Get price from base variation
 */
export const getBaseVariationPrice = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null
): string | null => {
  const baseVariation = getBaseVariation(variations);
  return baseVariation?.price || null;
};

/**
 * Get formatted price number from base variation (parsed as float)
 */
export const getBaseVariationPriceNumber = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null
): number | null => {
  const price = getBaseVariationPrice(variations);
  if (!price) return null;

  const parsed = parseFloat(price);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Get all available variation keys (shares/multipliers)
 */
export const getAvailableVariationKeys = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null
): string[] => {
  if (!variations || typeof variations !== 'object') {
    return [];
  }
  return Object.keys(variations);
};

/**
 * Get all variation values as array
 */
export const getAllVariations = <T extends VariationData>(variations: VariationsRecord<T> | undefined | null): T[] => {
  if (!variations || typeof variations !== 'object') {
    return [];
  }
  return Object.values(variations);
};

/**
 * Get the appropriate variation for a given slider value based on discount intervals
 * Each variation defines a discount that applies from its multiplier value onwards
 * until the next variation is reached
 */
export const getVariationForSliderValue = <T extends VariationData>(
  variations: VariationsRecord<T> | undefined | null,
  sliderValue: number
): T | null => {
  if (!variations || typeof variations !== 'object') {
    return null;
  }

  const variationEntries = Object.entries(variations)
    .map(([key, variation]) => ({
      multiplier: parseInt(key, 10),
      variation,
    }))
    .filter((entry) => !isNaN(entry.multiplier))
    .sort((a, b) => b.multiplier - a.multiplier); // Sort descending

  if (variationEntries.length === 0) return null;

  // Find the highest multiplier that is <= sliderValue
  for (const entry of variationEntries) {
    if (entry.multiplier <= sliderValue) {
      return entry.variation;
    }
  }

  // If no suitable variation found, return the lowest multiplier variation
  return variationEntries[variationEntries.length - 1].variation;
};
